import {Filter} from '@github-ui/filter'
import {hpcAttrs} from '@github-ui/hpc'
import {useQuery} from '@github-ui/react-query'
import {SoftNavOnlyLink as Link, useParams} from '@github-ui/react-router'
import {useRepoIssuesSidebarLayoutEnabled} from '@github-ui/repo-issues-page-layout/useSidebarLayoutEnabled'
import {useNavigate} from '@github-ui/use-navigate'
import {ArrowLeftIcon} from '@primer/octicons-react'
import {Heading} from '@primer/react'
import {clsx} from 'clsx'
import {type MouseEventHandler, useCallback, useEffect, useRef} from 'react'

import {LABELS} from '../../constants'
import {useCreateSavedViewMutation} from '../../hooks/use-create-saved-view-mutation'
import {useSavedViewAnalytics} from '../../hooks/use-saved-view-analytics'
import type {UseSavedViewEditorReturn} from '../../hooks/use-saved-view-editor'
import {useSavedViewFilterProviders} from '../../hooks/use-saved-view-filter-providers'
import {savedViewsQueryOptions} from '../../queries'
import {SAVED_VIEW_OPEN_SOURCES, type SavedViewOpenLocationState} from '../../saved-view-open-source'
import type {SavedViewIssuesRenderer} from '../../types'
import {SavedViewMutationErrorBanner} from '../dialogs/SavedViewMutationErrorBanner'
import {ShortcutEditingHeader} from '../ShortcutEditingHeader'
import styles from './RepositoryViewNewPage.module.css'
import {RepositoryViewNewPreview} from './RepositoryViewNewPreview'

interface RepositoryViewNewContentProps {
  editor: UseSavedViewEditorReturn
  confirmDiscardIfDirty: () => Promise<boolean>
  onBackLinkClick?: MouseEventHandler<HTMLAnchorElement>
  IssuesRenderer: SavedViewIssuesRenderer
}

export function RepositoryViewNewContent({
  editor,
  confirmDiscardIfDirty,
  onBackLinkClick,
  IssuesRenderer,
}: RepositoryViewNewContentProps) {
  const {owner = '', repo = ''} = useParams<{owner: string; repo: string}>()
  const navigate = useNavigate()
  const sidebarLayoutEnabled = useRepoIssuesSidebarLayoutEnabled()

  const viewsPath = `/${owner}/${repo}/issues/views`

  const {data: savedViewsData} = useQuery(savedViewsQueryOptions({owner, repo}))
  const viewerCanManageViews = savedViewsData?.viewerCanManageViews
  useEffect(() => {
    if (viewerCanManageViews === false) {
      navigate(viewsPath, {replace: true})
    }
  }, [navigate, viewerCanManageViews, viewsPath])

  const {trackNewVisited} = useSavedViewAnalytics()
  const hasTrackedVisitRef = useRef(false)
  useEffect(() => {
    if (hasTrackedVisitRef.current) return
    hasTrackedVisitRef.current = true
    trackNewVisited()
  }, [trackNewVisited])

  const {shortcut, setShortcutValue, previewQuery, commitPreviewQuery, previewQueryContextValue} = editor

  const filterProviders = useSavedViewFilterProviders({owner, repo})

  const {mutate, isPending, error} = useCreateSavedViewMutation({owner, repo})

  const handleDismiss = useCallback(async () => {
    if (await confirmDiscardIfDirty()) {
      navigate(viewsPath)
    }
  }, [confirmDiscardIfDirty, navigate, viewsPath])

  const handleSave = useCallback(() => {
    mutate(
      {
        name: shortcut.name.trim(),
        query: shortcut.query.trim(),
        description: shortcut.description?.trim() || undefined,
        icon: shortcut.icon,
        color: shortcut.color,
        source: 'new',
      },
      {
        onSuccess: ({savedView}) => {
          const openState: SavedViewOpenLocationState = {savedViewOpenSource: SAVED_VIEW_OPEN_SOURCES.created}
          navigate(`${viewsPath}/${savedView.id}`, {state: openState})
        },
      },
    )
  }, [mutate, navigate, viewsPath, shortcut])

  if (viewerCanManageViews === false) {
    return null
  }

  return (
    <div className={clsx(!sidebarLayoutEnabled && styles.middlePaneWrapper)} {...hpcAttrs()}>
      {!sidebarLayoutEnabled && (
        <>
          <Link to={viewsPath} className={styles.backLink} onClick={onBackLinkClick}>
            <ArrowLeftIcon />
            {LABELS.views.backToViews}
          </Link>
          <Heading as="h1" variant="medium" className={styles.heading}>
            {LABELS.views.createLink}
          </Heading>
        </>
      )}
      <div className={styles.middlePaneGrid}>
        <SavedViewMutationErrorBanner
          message={error ? LABELS.views.createError : null}
          onRetry={handleSave}
          isRetrying={isPending}
        />
        <ShortcutEditingHeader
          shortcut={shortcut}
          setShortcutValue={setShortcutValue}
          onSave={handleSave}
          onDismiss={handleDismiss}
          isSaving={isPending}
          helperText={LABELS.views.createDescription}
          renderFilter={({inputRef, className}) => (
            <Filter
              id="repo-view-new-filter"
              className={className}
              inputRef={inputRef}
              label={LABELS.views.filterQueryLabel}
              placeholder={LABELS.views.filterQueryPlaceholder}
              variant="input"
              providers={filterProviders}
              filterValue={shortcut.query}
              onChange={value => setShortcutValue('query', value)}
              onSubmit={request => commitPreviewQuery(request.raw)}
              settings={{aliasMatching: true, groupAndKeywordSupport: true}}
            />
          )}
        />
        <RepositoryViewNewPreview
          owner={owner}
          repo={repo}
          query={previewQuery}
          previewQueryContextValue={previewQueryContextValue}
          IssuesRenderer={IssuesRenderer}
        />
      </div>
    </div>
  )
}
